<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useApiData } from '../../composables/useApiData';
import { usePortalAccount } from '../../composables/usePortalAccount';
import axios from 'axios';
import Auth from 'dashboard/api/auth';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
});

// Utiliser le composable pour récupérer l'account_id (portail ou route)
const { accountId } = usePortalAccount();

const { data, loading, error } = useApiData(
  computed(() => props.widget),
  accountId
);

const title = computed(() => props.widget.props?.title || 'Conversations');
const limit = computed(() => props.widget.props?.limit || 5);
const status = computed(() => props.widget.props?.status || 'open');

// État pour la conversation sélectionnée
const selectedConversation = ref(null);
const messages = ref([]);
const messagesLoading = ref(false);
const messagesError = ref(null);
const messagesContainer = ref(null);

// Watcher pour réinitialiser la sélection si le widget change
watch(
  () => props.widget,
  () => {
    selectedConversation.value = null;
    messages.value = [];
  },
  { deep: true }
);

const conversations = computed(() => {
  if (
    data.value?.mapped?.conversations &&
    Array.isArray(data.value.mapped.conversations)
  ) {
    return data.value.mapped.conversations.slice(0, limit.value);
  }
  if (data.value?.raw?.payload && Array.isArray(data.value.raw.payload)) {
    return data.value.raw.payload.slice(0, limit.value);
  }
  if (Array.isArray(data.value?.raw)) {
    return data.value.raw.slice(0, limit.value);
  }
  return [];
});

const getStatusClass = status => {
  const classes = {
    open: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    resolved: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    snoozed: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  };
  return classes[status] || classes.open;
};

const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}j`;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const formatMessageDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isYesterday =
    date.toDateString() ===
    new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);

  // Messages très récents (< 1 min)
  if (diffMins < 1) {
    return "À l'instant";
  }

  // Aujourd'hui
  if (isToday) {
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Hier
  if (isYesterday) {
    return `Hier ${date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  // Cette semaine
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays < 7) {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Plus ancien
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (Auth.hasAuthCookie()) {
    const authData = Auth.getAuthData();
    if (authData && authData['access-token']) {
      headers['access-token'] = authData['access-token'];
      headers['token-type'] = authData['token-type'];
      headers.client = authData.client;
      headers.expiry = authData.expiry;
      headers.uid = authData.uid;
    }
  }

  return headers;
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const selectConversation = async conversation => {
  selectedConversation.value = conversation;
  messages.value = [];
  messagesLoading.value = true;
  messagesError.value = null;

  try {
    const conversationId = conversation.id || conversation.display_id;
    const url = `/api/v1/accounts/${accountId.value}/conversations/${conversationId}/messages`;

    const response = await axios.get(url, {
      headers: getAuthHeaders(),
    });

    messages.value = response.data?.payload || response.data || [];
    messagesError.value = null;

    // Scroll vers le bas après chargement
    scrollToBottom();
  } catch (err) {
    const errorMessage =
      err.response?.data?.error ||
      err.response?.data?.message ||
      err.message ||
      'Erreur lors de la récupération des messages';
    messagesError.value = errorMessage;
    console.error('Erreur lors de la récupération des messages:', err);
  } finally {
    messagesLoading.value = false;
  }
};

// Watch pour scroller quand de nouveaux messages arrivent
watch(
  () => messages.value.length,
  () => {
    scrollToBottom();
  }
);

const goBackToList = () => {
  selectedConversation.value = null;
  messages.value = [];
  messagesError.value = null;
};

const isAgentMessage = message => {
  return message.message_type === 'outgoing' || message.message_type === 1;
};

const isIncomingMessage = message => {
  return message.message_type === 'incoming' || message.message_type === 0;
};

const shouldShowAvatar = (message, index) => {
  // Toujours afficher l'avatar pour le dernier message de la série
  if (index === messages.value.length - 1) return true;

  const nextMessage = messages.value[index + 1];
  if (!nextMessage) return true;

  // Si le message suivant est du même type, on masque l'avatar
  // sauf s'il s'est écoulé plus de 5 minutes
  const isSameType = message.message_type === nextMessage.message_type;
  if (!isSameType) return true;

  const currentDate = new Date(message.created_at || message.timestamp);
  const nextDate = new Date(nextMessage.created_at || nextMessage.timestamp);
  const diffMins = (nextDate - currentDate) / 60000;

  return diffMins > 5;
};

const getBubbleClass = (message, index) => {
  const isIncoming = isIncomingMessage(message);
  const nextMessage = messages.value[index + 1];
  const prevMessage = messages.value[index - 1];

  const isSameNext =
    nextMessage && message.message_type === nextMessage.message_type;
  const isSamePrev =
    prevMessage && message.message_type === prevMessage.message_type;

  let classes = isIncoming
    ? 'bg-slate-100 text-slate-900 border border-slate-200'
    : 'bg-indigo-500 text-white shadow-md shadow-indigo-500/20 border border-indigo-400/20';

  // Gestion des coins arrondis (plus modernes)
  if (isIncoming) {
    if (isSamePrev && isSameNext) classes += ' rounded-r-2xl rounded-l-md';
    else if (isSamePrev)
      classes += ' rounded-r-2xl rounded-bl-2xl rounded-tl-md';
    else if (isSameNext)
      classes += ' rounded-r-2xl rounded-tl-2xl rounded-bl-md';
    else classes += ' rounded-2xl rounded-tl-md';
  } else if (isSamePrev && isSameNext) classes += ' rounded-l-2xl rounded-r-md';
  else if (isSamePrev) classes += ' rounded-l-2xl rounded-br-2xl rounded-tr-md';
  else if (isSameNext) classes += ' rounded-l-2xl rounded-tr-2xl rounded-br-md';
  else classes += ' rounded-2xl rounded-tr-md';

  return classes;
};
</script>

<template>
  <div class="h-full flex flex-col font-inter group">
    <!-- Vue de la conversation sélectionnée -->
    <div v-if="selectedConversation" class="h-full flex flex-col">
      <!-- Header moderne et épuré -->
      <div class="flex items-center gap-4 pb-4 mb-6 border-b border-slate-200">
        <button
          class="h-10 w-10 flex items-center justify-center transition-all duration-200 rounded-xl bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 group/back"
          @click="goBackToList"
        >
          <span
            class="i-lucide-chevron-left h-5 w-5 transition-transform group-hover/back:-translate-x-0.5"
          />
        </button>

        <!-- Avatar -->
        <div
          class="h-12 w-12 shrink-0 rounded-2xl overflow-hidden border border-slate-200 shadow-lg relative group/avatar bg-slate-50"
        >
          <img
            v-if="selectedConversation.contact?.avatar_url"
            :src="selectedConversation.contact.avatar_url"
            :alt="selectedConversation.contact.name"
            class="w-full h-full object-cover transition-transform duration-500 group-hover/avatar:scale-110"
          />
          <div
            v-else
            class="w-full h-full flex items-center justify-center text-slate-400"
          >
            <span class="i-lucide-user h-6 w-6" />
          </div>
        </div>

        <div class="flex-1 min-w-0">
          <h3
            class="text-sm font-bold text-slate-900 truncate leading-tight transition-colors"
          >
            {{
              selectedConversation.meta?.sender?.name ||
              selectedConversation.contact?.name ||
              'Conversation'
            }}
          </h3>
          <p
            v-if="selectedConversation.inbox"
            class="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1 truncate"
          >
            {{ selectedConversation.inbox.name }}
          </p>
        </div>

        <div
          class="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm bg-white"
          :class="getStatusClass(selectedConversation.status || 'open')"
        >
          {{ selectedConversation.status || 'open' }}
        </div>
      </div>

      <!-- Zone de messages -->
      <div
        ref="messagesContainer"
        class="flex-1 overflow-y-auto pr-2 -mr-2 scroll-smooth custom-scrollbar"
      >
        <div
          v-if="messagesLoading"
          class="flex items-center justify-center py-16"
        >
          <div class="flex flex-col items-center gap-4">
            <div
              class="h-10 w-10 border-2 border-slate-200 border-t-indigo-500 rounded-full animate-spin"
            />
            <span
              class="text-[10px] font-bold text-slate-500 uppercase tracking-widest"
              >Loading...</span>
          </div>
        </div>
        <div v-else class="space-y-3 pb-6 px-1">
          <div
            v-for="(message, index) in messages"
            :key="message.id || index"
            class="flex gap-3 group/msg relative"
            :class="[
              isIncomingMessage(message) ? 'justify-start' : 'justify-end',
              index > 0 &&
              messages[index - 1].message_type === message.message_type
                ? 'mt-1'
                : 'mt-6',
            ]"
          >
            <!-- Bulle -->
            <div
              class="flex flex-col max-w-[85%] group/bubble"
              :class="isIncomingMessage(message) ? 'items-start' : 'items-end'"
            >
              <div
                v-if="
                  isIncomingMessage(message) &&
                  message.sender?.name &&
                  (index === 0 ||
                    messages[index - 1].message_type !== message.message_type)
                "
                class="text-[10px] font-bold text-slate-500 mb-2 px-1 uppercase tracking-widest"
              >
                {{ message.sender.name }}
              </div>

              <div
                class="px-5 py-3 text-[13px] transition-all duration-200 relative z-10 leading-relaxed shadow-sm"
                :class="getBubbleClass(message, index)"
              >
                <div class="break-words whitespace-pre-wrap font-medium">
                  {{ message.content }}
                </div>
              </div>

              <div
                v-if="shouldShowAvatar(message, index)"
                class="text-[9px] font-bold text-slate-400 mt-2 px-1 uppercase tracking-[0.1em] opacity-0 group-hover/msg:opacity-100 transition-all duration-200"
              >
                {{ formatMessageDate(message.created_at || message.timestamp) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Vue liste -->
    <div v-else class="h-full flex flex-col">
      <div
        v-if="title"
        class="mb-6 pb-4 border-b border-slate-200 flex items-center justify-between px-1"
      >
        <h3 class="text-sm font-bold text-slate-900 tracking-tight">
          {{ title }}
        </h3>
        <div
          class="h-1.5 w-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)]"
        />
      </div>

      <div class="flex-1 overflow-auto custom-scrollbar pr-1">
        <div
          v-if="loading && conversations.length === 0"
          class="flex items-center justify-center py-16"
        >
          <div class="flex flex-col items-center gap-4">
            <div
              class="h-10 w-10 border-2 border-slate-200 border-t-indigo-500 rounded-full animate-spin"
            />
            <span
              class="text-[10px] font-bold text-slate-500 uppercase tracking-widest"
              >Loading...</span>
          </div>
        </div>

        <div v-else class="space-y-3 pb-4">
          <div
            v-for="(conversation, index) in conversations"
            :key="conversation.id || index"
            class="group/item flex items-start gap-4 p-4 transition-all duration-300 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 cursor-pointer"
            @click="selectConversation(conversation)"
          >
            <div class="relative flex-shrink-0">
              <div
                class="h-12 w-12 rounded-xl border border-slate-200 bg-white overflow-hidden shadow-lg transition-all duration-500 group-hover/item:scale-105 group-hover/item:border-indigo-300"
              >
                <img
                  v-if="conversation.contact?.avatar_url"
                  :src="conversation.contact.avatar_url"
                  :alt="conversation.contact.name"
                  class="w-full h-full object-cover transition-transform duration-500 group-hover/item:scale-110"
                />
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center text-slate-400"
                >
                  <span class="i-lucide-user h-6 w-6" />
                </div>
              </div>
              <div
                class="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white shadow-sm flex items-center justify-center"
                :class="{
                  'bg-amber-500': (conversation.status || 'open') === 'open',
                  'bg-slate-400':
                    (conversation.status || 'open') === 'resolved',
                  'bg-amber-500': (conversation.status || 'open') === 'pending',
                }"
              />
            </div>

            <div class="flex-1 min-w-0 pt-0.5">
              <div class="flex items-center justify-between gap-3 mb-1.5">
                <div
                  class="text-[13px] font-bold truncate text-slate-900 group-hover/item:text-indigo-600 transition-colors"
                >
                  {{
                    conversation.meta?.sender?.name ||
                    conversation.contact?.name ||
                    'Contact'
                  }}
                </div>
                <span
                  class="text-[9px] font-bold text-slate-500 whitespace-nowrap uppercase tracking-widest"
                >
                  {{
                    formatDate(
                      conversation.last_activity_at || conversation.updated_at
                    )
                  }}
                </span>
              </div>

              <div
                class="text-[13px] font-medium text-slate-600 line-clamp-1 mb-3 leading-snug group-hover/item:text-slate-900 transition-colors"
              >
                <span
                  v-if="
                    conversation.messages && conversation.messages.length > 0
                  "
                >
                  {{
                    conversation.messages[conversation.messages.length - 1]
                      ?.content || conversation.last_message
                  }}
                </span>
                <span v-else-if="conversation.last_message">{{
                  conversation.last_message
                }}</span>
                <span v-else class="italic opacity-50">No messages</span>
              </div>

              <div class="flex items-center gap-2">
                <div
                  v-if="conversation.inbox"
                  class="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-500 bg-white border border-slate-200 px-2 py-1 rounded-md"
                >
                  <span class="i-lucide-inbox h-3 w-3 opacity-70" />
                  {{ conversation.inbox.name }}
                </div>
              </div>
            </div>

            <div
              class="flex-shrink-0 self-center opacity-0 group-hover/item:opacity-100 -translate-x-2 group-hover/item:translate-x-0 transition-all duration-300"
            >
              <span class="i-lucide-arrow-right h-4 w-4 text-indigo-500" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
